import {
  Container,
  CreateNewOrganizationForm,
  MyPendingInviteList,
} from "@repo/lib-web-ui";
import { type CombinedError, useMutation, useQuery } from "urql";

import { Link } from "@tanstack/react-router";
import React from "react";
import { Users } from "lucide-react";
import type { API_ERRORS } from "@repo/lib-common";
import { graphql } from "@repo/lib-web-gql";
import { useSupabaseUser } from "@repo/lib-web-common";

/** What the user sees when accepting an invitation fails, by API error code. */
const ACCEPT_INVITATION_ERRORS: Partial<
  Record<keyof typeof API_ERRORS, string>
> = {
  ORGANIZATION_INVITATION_EMAIL_MISMATCH:
    "This invitation was sent to a different email address. Sign in with that address to accept it.",
  ORGANIZATION_INVITATION_EMAIL_UNCONFIRMED:
    "Confirm your email address before accepting this invitation.",
  ORGANIZATION_INVITATION_EXPIRED:
    "This invitation has expired. Ask for a new one.",
  ORGANIZATION_INVITATION_ACCEPTED: "This invitation was already accepted.",
  ORGANIZATION_INVITATION_UNKNOWN: "This invitation no longer exists.",
};

const acceptInvitationErrorMessage = (error: CombinedError): string => {
  const [first] = error.graphQLErrors;
  const code = first?.extensions?.code;
  const known =
    typeof code === "string" &&
    Object.prototype.hasOwnProperty.call(ACCEPT_INVITATION_ERRORS, code)
      ? ACCEPT_INVITATION_ERRORS[code as keyof typeof API_ERRORS]
      : undefined;
  return known ?? first?.message ?? error.message;
};

const AcceptInvitation = graphql(`
  mutation AcceptInvitation($id: UUID!) {
    apiAcceptOrganizationInvitation(invitationId: $id) {
      organizationId
      invitationId
    }
  }
`);

const DeleteInvitation = graphql(`
  mutation DeleteInvitation($id: UUID!) {
    deleteFromOrganizationInvitationsCollection(filter: { id: { eq: $id } }) {
      affectedCount
    }
  }
`);

const CreateOrganization = graphql(`
  mutation CreateOrganization($name: String!) {
    apiCreateOrganization(org: { name: $name }) {
      __typename
    }
  }
`);

const MyOrgsAndInvites = graphql(`
  query MyOrgs($id: UUID!, $email: String!) {
    organizationMembersCollection(filter: { profileId: { eq: $id } }) {
      edges {
        node {
          id
          organization {
            name
            id
          }
        }
      }
    }

    organizationInvitationsCollection(
      filter: { email: { eq: $email }, status: { eq: pending } }
    ) {
      ...MyPendingInviteListFragment
      edges {
        node {
          id
        }
      }
    }
  }
`);

export function CreateOrJoinOrg() {
  const user = useSupabaseUser();
  const myId = user.user?.id;
  const email = user.user?.email;
  // const client = useClient();

  const [{ data }] = useQuery({
    query: MyOrgsAndInvites,
    pause: !myId && !email,
    variables: { id: myId!, email: email! },
  });
  const [_, create] = useMutation(CreateOrganization);
  const [_acceptOperation, acceptMutation] = useMutation(AcceptInvitation);
  const accept = React.useCallback(
    async (id: string) => {
      const res = await acceptMutation(
        { id },
        {
          additionalTypenames: [
            "Profiles",
            "ProfilesConnection",
            "Organizations",
            "OrganizationsConnection",
            "organizationInvitations",
          ],
        },
      );
      // MyPendingInviteList toasts the `message` of what this returns.
      if (res.error)
        return { message: acceptInvitationErrorMessage(res.error) };
      if (!res.data) return { message: "No data returned" };
      // Failures arrive as GraphQL errors; any data means it was accepted.
      return undefined;
    },
    [acceptMutation],
  );
  const [_rejectOperation, reject] = useMutation(DeleteInvitation);
  const [name, setName] = React.useState("");
  return (
    <Container className="container max-w-5xl py-10">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold tracking-tight">
          Welcome to Your Dashboard
        </h1>
        <p className="mt-2 text-muted-foreground">
          Create a new organization or join an existing one
        </p>
      </div>

      <CreateNewOrganizationForm
        onCreate={async () =>
          create(
            { name },
            {
              additionalTypenames: ["Profiles", "Organizations"],
            },
          )
        }
        {...{ name, setName }}
      />

      {!!data?.organizationInvitationsCollection?.edges?.length && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold tracking-tight">
              <Users className="mr-2 inline-block h-5 w-5" />
              Pending Invitations
            </h2>
          </div>

          <MyPendingInviteList
            myId={myId!}
            dbFragment={data?.organizationInvitationsCollection}
            actions={{
              accept,
              decline: async (id: string) => reject({ id }),
            }}
          />
        </div>
      )}
      <Link to={"/sign-out"} className="link underline">
        sign out
      </Link>
    </Container>
  );
}
